import { useState } from "react";
import { ArrowRight, Lightning, Trophy, ChartBar, Clock, TrendUp, Crosshair } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { trackEvent } from "@/App";
import SEO from "@/components/SEO";
import EmailCaptureModal from "@/components/EmailCaptureModal";
import PublicNav from "@/components/PublicNav";
import { NewsletterSignup } from "@/components/NewsletterSignup";

const PILLAR_GUIDES = [
  {
    href: "/guide/dynasty-startup-draft",
    title: "Startup Draft Guide",
    subtitle: "What Actually Wins",
    stat: "1,180",
    statLabel: "startup drafts analyzed",
    icon: <Trophy size={18} weight="fill" />,
    color: "#ccff00",
    heroImage: "/hero-images/guide-startup-draft.jpg",
    desc: "Real ADP, positional distribution, and round-by-round strategy from champion leagues.",
    findings: ["Josh Allen #1 ADP at 3.3", "46.5% of Round 1 picks are QBs", "WR is the biggest value allocation at 35%"],
  },
  {
    href: "/guide/dynasty-trade-deadline",
    title: "Trade Deadline Guide",
    subtitle: "What Champions Did",
    stat: "167K",
    statLabel: "real trades analyzed",
    icon: <Clock size={18} weight="fill" />,
    color: "#45b7d1",
    heroImage: "/hero-images/guide-trade-deadline.jpg",
    desc: "Deadline behavior from championship-winning leagues. When to buy, when to sell, and how.",
    findings: ["77.9% of champion leagues traded at deadline", "Peak activity at Week 11", "35.9% of in-season trades in deadline window"],
  },
  {
    href: "/guide/dynasty-rookie-draft",
    title: "Rookie Draft Guide",
    subtitle: "Pick Values & What Hits",
    stat: "5,975",
    statLabel: "rookie drafts analyzed",
    icon: <ChartBar size={18} weight="fill" />,
    color: "#4ecdc4",
    heroImage: "/hero-images/guide-rookie-draft.jpg",
    desc: "What rookie picks are actually worth and how often each slot produces a dynasty asset.",
    findings: ["Jeanty consensus 1.01", "RBs dominate the top 5", "Early 1sts carry the biggest premium"],
  },
  {
    href: "/guide/dynasty-rebuild",
    title: "Rebuild Guide",
    subtitle: "How Long It Actually Takes",
    stat: "6,976",
    statLabel: "champion rosters studied",
    icon: <TrendUp size={18} weight="fill" />,
    color: "#f9ca24",
    heroImage: "/hero-images/guide-rebuild.jpg",
    desc: "The honest timeline for rebuilds. When to start, how to sell, and how to get back fastest.",
    findings: ["Champion avg age: 27.4", "Top 3 hold 36.5% of value", "Sell in-season, not offseason"],
  },
];

const GUIDE_SECTIONS = [
  {
    title: "WHAT MAKES DYNASTY DIFFERENT?",
    content: `Unlike redraft leagues where you start fresh each year, dynasty leagues carry your roster year over year — making every trade a long-term investment decision. A player's value isn't just about this season's production; it's about age, contract, opportunity, and future potential.

This is why dynasty trading is so complex — and why most managers either make no trades (afraid of getting it wrong) or make bad trades (going with gut feel instead of data). Dynasty Trade Maker exists to solve both problems.`,
  },
  {
    title: "THE #1 RULE OF DYNASTY TRADING",
    content: `**Never trade without knowing the values.** This sounds obvious, but the majority of dynasty trades happen without either side checking consensus trade values. The result? One side almost always overpays.

FantasyCalc aggregates real trade data from over 150,000 Sleeper leagues to produce consensus dynasty values. These aren't opinions from a single analyst — they're derived from what actual managers are trading players for.

Dynasty Trade Maker puts these values at your fingertips for every player and pick on every roster in your league.`,
  },
  {
    title: "UNDERSTANDING PLAYER VALUES",
    content: `Dynasty values typically range from 0 to 11,000+. Here's how to think about the tiers:

**Elite (7,000+):** Franchise cornerstones. Top 5 at their position.

**High (4,000-7,000):** Strong starters. Players you build around.

**Mid (2,000-4,000):** Solid contributors. Weekly starters in most leagues.

**Low (500-2,000):** Depth pieces. Bench stashes, handcuffs, older veterans.

**Key insight:** In dynasty, a player worth 8,000 is NOT the same as two players worth 4,000 each. Consolidating value (trading 2-for-1 to get a star) is almost always the winning strategy.`,
  },
  {
    title: "WHEN TO BUY VS. SELL",
    content: `**Buy low when:**
- A player has a bad stretch but their talent hasn't changed
- A player gets injured (value drops, but recovers when they return)
- A rookie is in a timeshare early in the season

**Sell high when:**
- A player is having an unsustainable hot streak
- A veteran is 28+ and at peak value (before the age cliff)
- Your team is rebuilding and you can convert a 29-year-old into a 1st round pick`,
  },
  {
    title: "THE SUPERFLEX PREMIUM",
    content: `In Superflex leagues, starting QBs are dramatically more valuable than in 1QB formats. Josh Allen might be worth 5,500 in 1QB but 10,200+ in Superflex.

**Never trade a starting QB without getting a QB back** (unless you have 3+). Dynasty Trade Maker automatically detects your league's Superflex setting and adjusts all values accordingly.`,
  },
  {
    title: "USING DRAFT PICKS AS CURRENCY",
    content: `Draft picks are the universal currency of dynasty trading. Your 1.01 is worth ~7,000 while the 1.12 is worth ~1,200. This matters — a "1st round pick" is NOT a "1st round pick" unless you know the draft position.

**Future picks (2027, 2028) have generic values** since draft order isn't determined yet. Use picks to balance trades and signal your team's direction.`,
  },
];

const MORE_GUIDES = [
  { href: "/guide/startup-draft-strategy", title: "Startup Draft Strategy Guide", desc: "Round-by-round plan, position priorities, and tier-based drafting" },
  { href: "/guide/superflex-strategy", title: "Dynasty Superflex Strategy Guide", desc: "How to draft and trade in Superflex — with live QB value comparisons" },
  { href: "/guide/dynasty-vs-redraft", title: "Dynasty vs. Redraft: What's Different", desc: "Side-by-side comparison of the two formats" },
  { href: "/tools/draft-archetype", title: "Draft Archetype Finder", desc: "Which champion draft build matches your roster?" },
  { href: "/tools/trade-fairness", title: "Trade Fairness Analyzer", desc: "Is your trade fair? Data from 125K graded trades" },
  { href: "/compare/dynasty-trade-value-tools", title: "Trade Value Tools Compared", desc: "KTC vs FantasyCalc vs Dynasty Trade Maker — honest breakdown" },
  { href: "/guide/sleeper-setup", title: "How to Connect Your Sleeper League", desc: "Step-by-step setup in under a minute — no password needed" },
];

export default function GuidePage() {
  const [showEmailModal, setShowEmailModal] = useState(false);
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO page="guide" />
      <EmailCaptureModal open={showEmailModal} onClose={() => setShowEmailModal(false)} source="guide-page" />
      <PublicNav />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00]">DATA-BACKED DYNASTY GUIDES</span>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-white uppercase mt-2 mb-4" data-testid="guide-title">
            THE COMPLETE GUIDE TO <span className="text-[#ccff00]">WINNING DYNASTY TRADES</span>
          </h1>
          <p className="text-base text-[#a3a3a3] max-w-xl mx-auto">Real data from 6,976 championship rosters, 167K trades, and 7,000+ real drafts. Not opinions — outcomes.</p>
        </div>

        {/* Pillar Guide Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-14" data-testid="pillar-guides">
          {PILLAR_GUIDES.map((g) => (
            <Link
              key={g.href}
              to={g.href}
              className="group border border-[#262626] bg-[#171717] hover:border-[#ccff00]/40 rounded-sm no-underline transition-all overflow-hidden"
              data-testid={`pillar-${g.href.split("/").pop()}`}
            >
              {g.heroImage && (
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={g.heroImage} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span style={{ color: g.color }}>{g.icon}</span>
                    <div>
                      <p className="text-sm font-bold text-white">{g.title}</p>
                      <p className="text-[10px] text-[#666] uppercase tracking-wider">{g.subtitle}</p>
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-[#555] group-hover:text-[#ccff00] transition-colors mt-1" />
                </div>
                <p className="text-xs text-[#888] mb-3">{g.desc}</p>
                <div className="flex items-end gap-3 mb-3">
                  <span className="text-2xl font-bold font-mono" style={{ color: g.color }}>{g.stat}</span>
                  <span className="text-[10px] text-[#555] pb-0.5">{g.statLabel}</span>
                </div>
                <div className="space-y-1">
                  {g.findings.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[10px] text-[#666]">
                      <Crosshair size={8} weight="bold" style={{ color: g.color }} className="shrink-0" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="h-0.5 w-full" style={{ background: `linear-gradient(to right, ${g.color}, transparent)` }} />
            </Link>
          ))}
        </div>

        {/* Dynasty 101 Content */}
        <div className="mb-12">
          <h2 className="font-heading text-2xl text-white uppercase tracking-tight mb-6 text-center">DYNASTY TRADING 101</h2>
          <div className="space-y-10">
            {GUIDE_SECTIONS.map((section, idx) => (
              <div key={idx} className="border-l-2 border-[#262626] pl-6 hover:border-[#ccff00] transition-colors">
                <h3 className="font-heading text-lg sm:text-xl text-white uppercase tracking-tight mb-3">{section.title}</h3>
                <div className="text-sm text-[#a3a3a3] leading-relaxed whitespace-pre-line">
                  {section.content.split("**").map((part, i) =>
                    i % 2 === 0 ? part : <strong key={i} className="text-white">{part}</strong>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-10 border-t border-b border-[#262626] mb-12">
          <h2 className="font-heading text-2xl text-white uppercase mb-3">PUT THIS INTO PRACTICE</h2>
          <p className="text-sm text-[#a3a3a3] mb-6 max-w-md mx-auto">Import your Sleeper leagues and see real values, AI trade suggestions, and contender rankings for your actual teams.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={() => { trackEvent("try_free_click"); window.location.href = "/"; }} className="h-11 px-8 bg-[#ccff00] text-black hover:bg-[#b8e600] rounded-sm text-sm font-bold">
              <Lightning size={16} weight="bold" className="mr-2" /> TRY FREE
            </Button>
            <Link to="/features" className="text-xs text-[#a3a3a3] hover:text-[#ccff00] no-underline flex items-center gap-1">
              SEE ALL FEATURES <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>

        {/* More Guides & Tools */}
        <div className="mb-10">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-4">MORE GUIDES & TOOLS</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {MORE_GUIDES.map((g) => (
              <Link key={g.href} to={g.href} className="group p-4 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline transition-colors rounded-sm">
                <p className="text-sm text-white font-semibold mb-1 group-hover:text-[#ccff00] transition-colors">{g.title}</p>
                <p className="text-[11px] text-[#666] leading-relaxed">{g.desc}</p>
              </Link>
            ))}
            <button onClick={() => setShowEmailModal(true)} className="group p-4 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 transition-colors text-left rounded-sm" data-testid="guide-link-cheatsheet">
              <p className="text-sm text-white font-semibold mb-1 group-hover:text-[#ccff00] transition-colors">Startup Draft Cheat Sheet (PDF)</p>
              <p className="text-[11px] text-[#666] leading-relaxed">Printable tier-by-tier startup draft rankings — free with email</p>
            </button>
          </div>
        </div>

        {/* Newsletter */}
        <NewsletterSignup source="guide-hub" className="mt-8" />
      </div>
    </div>
  );
}
